import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faMagnifyingGlass,
  faTags,
  faTruckFast,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { reportKeys, searchApi } from '../../features/reports/reportsApi';
import { ORDER_STATUS_LABELS } from '../../features/orders/labels';
import { ROUTES, routeTo } from '../../routes/paths';
import type { OrderStatus, SearchHit, SearchHitType } from '../../types/api';

/** Mínimo del backend. Con una letra la consulta recorre las tablas enteras. */
const MIN_LENGTH = 2;

/** Espera antes de consultar. Una consulta por tecla sería una por letra. */
const DEBOUNCE_MS = 250;

const ICONS: Record<SearchHitType, IconDefinition> = {
  PRODUCT: faTags,
  ORDER: faTruckFast,
  PROJECT: faBuilding,
};

const GROUP_LABELS: Record<SearchHitType, string> = {
  PRODUCT: 'Productos',
  ORDER: 'Órdenes',
  PROJECT: 'Obras',
};

function destinationFor(hit: SearchHit): string {
  switch (hit.type) {
    case 'PRODUCT':
      return routeTo(ROUTES.warehouse.productDetail, { id: hit.id });
    case 'ORDER':
      return routeTo(ROUTES.warehouse.orderDetail, { id: hit.id });
    default:
      return ROUTES.warehouse.projects;
  }
}

/** El estado de una orden llega en crudo; la lista lo enseña en español. */
function subtitleFor(hit: SearchHit): string {
  if (hit.type === 'ORDER' && hit.subtitle !== null) {
    return ORDER_STATUS_LABELS[hit.subtitle as OrderStatus] ?? hit.subtitle;
  }
  return hit.subtitle ?? '';
}

/**
 * Búsqueda global de la navbar.
 *
 * Busca sobre productos, órdenes y obras. Los resultados vienen acotados **por
 * entidad** y no en total: buscar «block» no puede devolver cinco productos y
 * ninguna orden, que es lo que pasaría con un tope global.
 *
 * Se consulta con retardo y no en cada tecla. Sin él, escribir «block» lanza
 * cinco búsquedas de las que cuatro se descartan.
 */
export function GlobalSearch(): ReactNode {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(term.trim());
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [term]);

  const query = useQuery({
    queryKey: reportKeys.search(debounced),
    queryFn: () => searchApi.search(debounced),
    enabled: debounced.length >= MIN_LENGTH,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onPointerDown = (event: MouseEvent): void => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const groups: [SearchHitType, SearchHit[]][] = query.data
    ? [
        ['PRODUCT', query.data.products],
        ['ORDER', query.data.orders],
        ['PROJECT', query.data.projects],
      ]
    : [];

  function open(hit: SearchHit): void {
    setIsOpen(false);
    setTerm('');
    void navigate(destinationFor(hit));
  }

  return (
    <div ref={boxRef} className="relative hidden lg:block">
      <label htmlFor="global-search" className="sr-only">
        Buscar en el sistema
      </label>

      <FontAwesomeIcon
        icon={faMagnifyingGlass}
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 w-3.5 -translate-y-1/2 text-muted"
      />

      <input
        id="global-search"
        type="search"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls="global-search-results"
        autoComplete="off"
        value={term}
        placeholder="Buscar producto, orden u obra"
        onChange={(event) => {
          setTerm(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          setIsOpen(true);
        }}
        className="h-10 w-72 rounded-card border border-line bg-background pl-9 pr-3 text-sm text-content placeholder:text-muted"
      />

      {isOpen && term.trim().length > 0 ? (
        <div
          id="global-search-results"
          role="listbox"
          className="absolute right-0 z-30 mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-card border border-line bg-surface py-1 shadow-lg"
        >
          {term.trim().length < MIN_LENGTH ? (
            <p className="px-3 py-4 text-sm text-muted">Escribe al menos dos caracteres.</p>
          ) : query.isPending ? (
            <p className="px-3 py-4 text-sm text-muted">Buscando…</p>
          ) : query.isError ? (
            <p className="px-3 py-4 text-sm text-danger">No se pudo buscar.</p>
          ) : query.data?.total === 0 ? (
            <p className="px-3 py-4 text-sm text-muted">Nada coincide con «{query.data.query}».</p>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {groups.map(([type, hits]) =>
                hits.length === 0 ? null : (
                  <li key={type}>
                    <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase text-muted">
                      {GROUP_LABELS[type]}
                    </p>
                    <ul>
                      {hits.map((hit) => (
                        <li key={`${hit.type}-${hit.id}`}>
                          <button
                            type="button"
                            role="option"
                            aria-selected="false"
                            onClick={() => {
                              open(hit);
                            }}
                            className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-primary-soft"
                          >
                            <FontAwesomeIcon
                              icon={ICONS[hit.type]}
                              aria-hidden="true"
                              className="w-3.5 shrink-0 text-muted"
                            />
                            <span className="min-w-0">
                              <span className="block truncate text-sm text-content">
                                {hit.title}
                              </span>
                              <span className="block truncate text-xs text-muted">
                                {[subtitleFor(hit), hit.context].filter(Boolean).join(' · ')}
                              </span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                ),
              )}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
